// pages/login.tsx
'use client'
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useSelector } from 'react-redux';
import { isAuthenticatedSelector } from '../store/authSlice';
import { useRouter } from 'next/navigation';
import { useAuthenticateMutation } from '../store/api';
import { RootState } from '../store/store';


const LoginPage: React.FC = () => {
    const router = useRouter()

    const emailRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const [authenticate, authRequest] = useAuthenticateMutation();

    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');



    const isAuthenticated = useSelector((state: RootState) => isAuthenticatedSelector(state.auth));
    const authenticating = useSelector((state: RootState) => state.auth.authenticating);

    useEffect(() => {
        if (isAuthenticated) {
            // Redirect to stats page
            router.push('/publisher-map');
        }
    }, [isAuthenticated, router]);

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value);
    };

    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(event.target.value);
    };

    const handleSubmit = async (event?: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
        if (event) {
            event.preventDefault();
        }
        console.log('Login button clicked - RTK Query authentication starting');
        
        // Basic validation
        if (!email.trim() || !password.trim()) {
            console.log('Form validation failed:', { email: email.trim(), password: password.trim() });
            return;
        }
        
        // Handle login logic here
        console.log('Submitting login with RTK Query:', { email, password: '***' });
        
        try {
            const result = await authenticate({ email, password }).unwrap();
            console.log('Authentication successful:', result);
            // The authSlice will handle the token storage and redirect
        } catch (error) {
            console.error('Authentication failed:', error);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
            <div className="w-full max-w-md">
                {/* Algorise Logo/Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
                        <span className="text-white text-xl font-bold">A</span>
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900">Algorise</h1>
                    <p className="text-gray-600 mt-2">Publisher Dashboard</p>
                </div>

                <Card className="shadow-lg border-0">
                    <CardHeader className="space-y-1">
                        <CardTitle className="text-2xl font-semibold text-center">
                            Sign in to your account
                        </CardTitle>
                        <CardDescription className="text-center">
                            Enter your credentials to access the dashboard
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {authenticating ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                <span className="ml-3 text-gray-600">Signing you in...</span>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-sm font-medium">
                                        Email Address
                                    </Label>
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        value={email}
                                        onChange={handleEmailChange}
                                        ref={emailRef}
                                        placeholder="Enter your email"
                                        className="h-11"
                                        disabled={authRequest.isLoading}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="password" className="text-sm font-medium">
                                        Password
                                    </Label>
                                    <Input
                                        id="password"
                                        name="password"
                                        type="password"
                                        autoComplete="current-password"
                                        required
                                        value={password}
                                        onChange={handlePasswordChange}
                                        ref={passwordRef}
                                        placeholder="Enter your password"
                                        className="h-11"
                                        disabled={authRequest.isLoading}
                                    />
                                </div>

                                {authRequest.error && (
                                    <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                                        {(authRequest.error as any)?.data?.message || 'Invalid email or password. Please try again.'}
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    className="w-full h-11 mt-6"
                                    disabled={authRequest.isLoading}
                                >
                                    {authRequest.isLoading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Signing in...
                                        </>
                                    ) : (
                                        "Sign in"
                                    )}
                                </Button>
                            </form>
                        )}
                    </CardContent>
                </Card>

                {/* Footer */}
                <div className="text-center mt-8 text-sm text-gray-500">
                    <p>&copy; 2024 Algorise. All rights reserved.</p>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
